import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { PHRASE_SETTING } from './phraseSetting';
import { check, repeatsAClass, tweensAxis } from '../skillUsage';

export const wordPopSettleSkill: CodeoSkill = {
    name: SKILL_NAMES.wordPopSettle,
    kind: 'codeo',
    description:
        'Build a spoken phrase word by word: each word appears at full opacity already a little ' +
        'below its place and settles hard, each one starting closer than the last so the line ' +
        'speeds up as it builds, then the whole line lifts out accelerating on the cut. The ' +
        'default grammar of a calm, typographic kinetic-type video, and the way to build any ' +
        'headline word by word on its spoken cues.',
    checkLooksFor:
        'words in repeated spans, each set visible and settled on y with power4.out or expo.out, never faded',
    documentUsesSkill: check(
        (script, markup) =>
            repeatsAClass(markup, 'span') &&
            tweensAxis(script, 'y') &&
            /ease:\s*['"](?:power4|expo)\.out/.test(script),
    ),
    instructions: `# Word Pop Settle

The phrase is built as it is said. Each word **appears on its cue at full opacity, already displaced** a little below its place, and settles into it fast and hard: most of the distance is covered in the first few frames, the last pixels take the rest. Nothing fades. The first word starts furthest away and each later word starts closer, so the line accelerates as it builds and the last word all but snaps into place.

When the phrase has been read, **the whole line lifts out together**, accelerating upward, and is gone on the cut to the next phrase. The next phrase pops in on the new ground the same way. That rhythm, pop and settle, hold, lift and cut, is the whole video's grammar; the hero moments are exceptions to it.

${PHRASE_SETTING}

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const SCENE = 2.2;
const CUES = [0.08, 0.25, 0.42, 0.58];   // each word's spoken onset
const CUT = 2.2;                         // when the next phrase takes the frame
const FIRST_OFFSET = 0.5;                // font-sizes below its place, for the first word
const DECAY = 0.45;                      // each later word starts this much closer

const phrase = document.querySelector('.phrase');
const words = phrase.querySelectorAll('.w');
const em = parseFloat(getComputedStyle(phrase).fontSize);
gsap.set(words, { autoAlpha: 0 });
words.forEach((word, i) => {
  const offset = FIRST_OFFSET * DECAY ** i * em;
  tl.set(word, { autoAlpha: 1 }, CUES[i]);
  tl.fromTo(word, { y: offset }, { y: 0, duration: 0.44, ease: 'power4.out', immediateRender: false }, CUES[i]);
});

// Out: the line lifts together, accelerating, gone on the cut.
tl.to(phrase, { y: -200, duration: 0.375, ease: 'expo.in' }, CUT - 0.375);

tl.set({}, {}, SCENE);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **A headline that holds.** In a graphic that is not cutting from phrase to phrase (a title card, a headline over a layout), drop the lift and hold the line to the end. Align and wrap it with the layout around it; the pop and the decay are the same.
- **No narration.** Without cues, place the words 0.12–0.18s apart; the decay still does its work.
- **A hero word.** A single word at the hero size rises further and settles slower: from 0.72 of its font size below its place, with \`expo.out\` over 0.62s. Use it for one-word beats ("Reads", "Thinks") and the first word of a graphic.
- **A second line.** A line that answers the first ("from Lumen5" under "The new AI teammate") arrives sideways instead: from 0.8 of its font size to the left, with \`power4.out\` over 0.58s, in the bold weight or the brand colour.
- **Fast speech.** Words closer than 0.1s apart still each get their own pop; never batch them into one tween.
- **The cut instead of the lift.** When the next phrase sits on a different ground, a hard cut at the end of the hold is enough; lift only when the motion carries into the next phrase. For a sideways carry use \`${SKILL_NAMES.velocityMatchedCut}\`.
- **Offsets in pixels.** GSAP does not convert \`em\` on \`x\`/\`y\`: compute the pixel offset from the line's font size, as above.
- **Frame rate.** The numbers are in seconds: 0.44s is about ten frames at 24fps and thirteen at 30fps.

## Pitfalls

- **No opacity tween.** The word is invisible, then visible on one frame, already moving. A fade is a softer, different effect, and it reads as late.
- **Decaying, not equal, offsets.** The same offset on every word makes the line bounce; the decay is what makes it land.
- **The cue is the arrival.** The word becomes visible on its cue and is readable a frame later; do not start it early to "lead" the voice.
- The word spans are transform-tweened, so they carry no CSS \`transform\`.
- Hold the finished phrase still. A phrase that keeps drifting after it has landed looks unfinished; motion during the hold belongs to the camera (\`${SKILL_NAMES.continuousPushIn}\`) or the ground.`,
};
